import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createReviewApi,
  fetchMyReviewsApi,
  fetchTutorReviewsApi,
} from '~/features/review/api/review.api';

import type { Review, ReviewInput } from '~/features/review/types/review.type';

export interface ReviewState {
  /** 지도사 상세에서 보는 후기 목록 */
  tutorReviews: Review[];
  isLoadingTutorReviews: boolean;
  /** 내가 쓴 후기 — "이 수업에 이미 썼는지" 판정 근거 */
  myReviews: Review[];
  isSubmitting: boolean;
  currentRequestId?: string;
}

const initialState: ReviewState = {
  tutorReviews: [],
  isLoadingTutorReviews: false,
  myReviews: [],
  isSubmitting: false,
};

export const fetchTutorReviews = createAsyncThunk<Review[], string>(
  'review/fetchTutorReviews',
  async (tutorId, { rejectWithValue }) => {
    try {
      return await fetchTutorReviewsApi(tutorId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchMyReviews = createAsyncThunk<Review[], void>(
  'review/fetchMyReviews',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMyReviewsApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const createReview = createAsyncThunk<Review, ReviewInput>(
  'review/createReview',
  async (input, { rejectWithValue }) => {
    try {
      return await createReviewApi(input);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const ReviewSlice = createSlice({
  initialState,
  name: 'review',
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTutorReviews.pending, (state, action) => {
        state.isLoadingTutorReviews = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchTutorReviews.fulfilled, (state, action) => {
        // 지도사를 빠르게 옮겨 다니면 늦게 끝난 옛 조회가 최신 목록을 덮어쓴다.
        if (state.currentRequestId !== action.meta.requestId) return;
        state.tutorReviews = action.payload;
        state.isLoadingTutorReviews = false;
      })
      .addCase(fetchTutorReviews.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.isLoadingTutorReviews = false;
      })
      .addCase(fetchMyReviews.fulfilled, (state, action) => {
        state.myReviews = action.payload;
      })
      .addCase(createReview.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.myReviews = [action.payload, ...state.myReviews];
        state.isSubmitting = false;
      })
      .addCase(createReview.rejected, state => {
        state.isSubmitting = false;
      });
  },
});

export default ReviewSlice;
